import type {
  AutoSelectItem,
  AutoSelectKey,
  CustomizeRequest,
  CustomizeResponse
} from '~/contracts/new-cars';

export type AutoSelectResult = {
  items: AutoSelectItem[];
  trigger: AutoSelectItem | null;
  updates: Partial<CustomizeRequest>;
};

function computeMatchingVehicleCount(response: CustomizeResponse): number {
  const fromInterior = response.interiorColors.reduce(
    (sum, c) => sum + c.count,
    0
  );

  if (fromInterior > 0) return fromInterior;

  const fromExterior = response.exteriorColors.reduce(
    (sum, c) => sum + c.count,
    0
  );

  if (fromExterior > 0) return fromExterior;

  const fromEngines = (response.engines ?? []).reduce(
    (sum, e) => sum + e.count,
    0
  );

  if (fromEngines > 0) return fromEngines;

  return response.driveTypes.reduce((sum, d) => sum + d.count, 0);
}

export function addToSelection(
  sel: CustomizeRequest,
  key: AutoSelectKey,
  code: string
): CustomizeRequest {
  switch (key) {
    case 'driveType':
      return { ...sel, driveType: code };
    case 'engine':
      return { ...sel, engineCode: code };
    case 'exterior':
      return { ...sel, exteriorColorCode: code };
    case 'interior':
      return { ...sel, interiorColorCode: code };
    case 'package':
      return {
        ...sel,
        packageOptionCodes: (sel.packageOptionCodes ?? []).concat(code)
      };
    default:
      throw new Error(`Unknown key: ${key}`);
  }
}

export function buildAutoSelectItem(
  key: AutoSelectKey,
  code: string,
  data: CustomizeResponse
): AutoSelectItem {
  switch (key) {
    case 'driveType': {
      const found = data.driveTypes.find(d => d.name === code);

      return {
        code,
        key,
        name: found?.name ?? code,
        priceAdd: found?.priceAdd ?? 0
      };
    }
    case 'engine': {
      const found = data.engines.find(e => e.code === code);

      return {
        code,
        key,
        name: found?.name ?? code,
        priceAdd: found?.priceAdd ?? 0
      };
    }
    case 'exterior': {
      const found = data.exteriorColors.find(c => c.code === code);

      return {
        code,
        key,
        name: found?.name ?? code,
        priceAdd: found?.priceAdd ?? 0
      };
    }
    case 'interior': {
      const found = data.interiorColors.find(c => c.code === code);

      return {
        code,
        key,
        name: found?.name ?? code,
        priceAdd: found?.priceAdd ?? 0
      };
    }
    case 'package': {
      const found = data.packageOptions.find(p => p.code === code);

      return {
        code,
        key,
        name: found?.name ?? code,
        priceAdd: found?.priceAdd ?? 0
      };
    }
    default:
      throw new Error(`Unknown key: ${key}`);
  }
}

/**
 * Constructs the final CustomizeRequest for the unavailable confirm path.
 * Starts from the stripped pending request, adds back items from currentOptions
 * that are still valid (count > 0) in the pending response, then applies
 * auto-select updates.
 */
export function buildUnavailableFinalState(
  pendingOptions: CustomizeRequest,
  currentOptions: CustomizeRequest,
  pendingResponse: CustomizeResponse,
  autoSelectUpdates: Partial<CustomizeRequest>
): CustomizeRequest {
  const result: CustomizeRequest = { ...pendingOptions };

  if (
    currentOptions.driveType &&
    !result.driveType &&
    (pendingResponse.driveTypes.find(d => d.name === currentOptions.driveType)
      ?.count ?? 0) > 0
  ) {
    result.driveType = currentOptions.driveType;
  }

  if (
    currentOptions.engineCode &&
    !result.engineCode &&
    (pendingResponse.engines.find(e => e.code === currentOptions.engineCode)
      ?.count ?? 0) > 0
  ) {
    result.engineCode = currentOptions.engineCode;
  }

  if (
    currentOptions.exteriorColorCode &&
    !result.exteriorColorCode &&
    (pendingResponse.exteriorColors.find(
      c => c.code === currentOptions.exteriorColorCode
    )?.count ?? 0) > 0
  ) {
    result.exteriorColorCode = currentOptions.exteriorColorCode;
  }

  if (
    currentOptions.interiorColorCode &&
    !result.interiorColorCode &&
    (pendingResponse.interiorColors.find(
      c => c.code === currentOptions.interiorColorCode
    )?.count ?? 0) > 0
  ) {
    result.interiorColorCode = currentOptions.interiorColorCode;
  }

  const pendingCodes = new Set(result.packageOptionCodes ?? []);
  const validCurrentCodes = (currentOptions.packageOptionCodes ?? []).filter(
    code =>
      !pendingCodes.has(code) &&
      (pendingResponse.packageOptions.find(p => p.code === code)?.count ?? 0) >
        0
  );

  if (validCurrentCodes.length > 0) {
    result.packageOptionCodes = (result.packageOptionCodes ?? []).concat(
      validCurrentCodes
    );
  }

  return { ...result, ...autoSelectUpdates };
}

export function hasPicks(sel: CustomizeRequest | null): boolean {
  return Boolean(
    sel?.driveType ||
      sel?.engineCode ||
      sel?.exteriorColorCode ||
      sel?.interiorColorCode ||
      (sel?.packageOptionCodes?.length ?? 0) > 0
  );
}

export function detectAutoSelectsFromResponse(
  response: CustomizeResponse,
  selectedOptions: CustomizeRequest,
  prevSelectedOptions: CustomizeRequest | null,
  triggerOverride?: AutoSelectItem | null
): AutoSelectResult | null {
  if (!hasPicks(selectedOptions)) return null;

  const matchingVehicleCount = computeMatchingVehicleCount(response);

  if (matchingVehicleCount === 0) return null;

  let trigger: AutoSelectItem | null;

  if (triggerOverride !== undefined) {
    trigger = triggerOverride;
  } else {
    trigger = null;

    if (
      selectedOptions.exteriorColorCode &&
      selectedOptions.exteriorColorCode !==
        prevSelectedOptions?.exteriorColorCode
    ) {
      trigger = buildAutoSelectItem(
        'exterior',
        selectedOptions.exteriorColorCode,
        response
      );
    } else if (
      selectedOptions.interiorColorCode &&
      selectedOptions.interiorColorCode !==
        prevSelectedOptions?.interiorColorCode
    ) {
      trigger = buildAutoSelectItem(
        'interior',
        selectedOptions.interiorColorCode,
        response
      );
    } else if (
      selectedOptions.engineCode &&
      selectedOptions.engineCode !== prevSelectedOptions?.engineCode
    ) {
      trigger = buildAutoSelectItem(
        'engine',
        selectedOptions.engineCode,
        response
      );
    } else if (
      selectedOptions.driveType &&
      selectedOptions.driveType !== prevSelectedOptions?.driveType
    ) {
      trigger = buildAutoSelectItem(
        'driveType',
        selectedOptions.driveType,
        response
      );
    } else {
      const prevCodes = new Set(prevSelectedOptions?.packageOptionCodes ?? []);
      const newCode = (selectedOptions.packageOptionCodes ?? []).find(
        c => !prevCodes.has(c)
      );

      if (newCode) {
        trigger = buildAutoSelectItem('package', newCode, response);
      }
    }

    if (!trigger) return null;
  }

  const updates: Partial<CustomizeRequest> = {};
  const items: AutoSelectItem[] = [];
  let hasForced = false;

  if (trigger?.key === 'engine') items.push(trigger);

  if (!selectedOptions.engineCode) {
    const available = (response.engines ?? []).filter(e => e.count > 0);

    if (available.length === 1) {
      hasForced = true;
      updates.engineCode = available[0].code;
      items.push(buildAutoSelectItem('engine', available[0].code, response));
    }
  }

  if (trigger?.key === 'exterior') items.push(trigger);

  if (!selectedOptions.exteriorColorCode) {
    const available = response.exteriorColors.filter(c => c.count > 0);

    if (available.length === 1) {
      hasForced = true;
      updates.exteriorColorCode = available[0].code;
      items.push(buildAutoSelectItem('exterior', available[0].code, response));
    }
  }

  if (trigger?.key === 'interior') items.push(trigger);

  if (!selectedOptions.interiorColorCode) {
    const available = response.interiorColors.filter(c => c.count > 0);

    if (available.length === 1) {
      hasForced = true;
      updates.interiorColorCode = available[0].code;
      items.push(buildAutoSelectItem('interior', available[0].code, response));
    }
  }

  if (trigger?.key === 'driveType') items.push(trigger);

  if (!selectedOptions.driveType) {
    const available = response.driveTypes.filter(d => d.count > 0);

    if (available.length === 1) {
      hasForced = true;
      updates.driveType = available[0].name;
      items.push(buildAutoSelectItem('driveType', available[0].name, response));
    }
  }

  if (trigger?.key === 'package') items.push(trigger);

  const currentCodes = new Set(selectedOptions.packageOptionCodes ?? []);
  const forcedPackageOptions = response.packageOptions.filter(
    p => p.count === matchingVehicleCount && !currentCodes.has(p.code)
  );

  if (forcedPackageOptions.length > 0) {
    hasForced = true;
    updates.packageOptionCodes = [
      ...(selectedOptions.packageOptionCodes ?? []),
      ...forcedPackageOptions.map(p => p.code)
    ];
    forcedPackageOptions.forEach(p =>
      items.push({
        code: p.code,
        key: 'package',
        name: p.name,
        priceAdd: p.priceAdd
      })
    );
  }

  return hasForced ? { items, trigger, updates } : null;
}

/**
 * Finds fields that changed in newFinalState vs currentOptions where the key
 * was NOT the user's trigger field (i.e., NOT changed in pendingOptions vs
 * currentOptions). Only unintended side effects are conflicts.
 */
export function detectConflicts(
  newFinalState: CustomizeRequest,
  currentOptions: CustomizeRequest,
  pendingOptions: CustomizeRequest
): AutoSelectItem[] {
  const conflicts: AutoSelectItem[] = [];

  if (
    currentOptions.driveType &&
    newFinalState.driveType !== currentOptions.driveType &&
    pendingOptions.driveType === currentOptions.driveType
  ) {
    conflicts.push({
      code: currentOptions.driveType,
      key: 'driveType',
      name: currentOptions.driveType,
      priceAdd: 0
    });
  }

  if (
    currentOptions.engineCode &&
    newFinalState.engineCode !== currentOptions.engineCode &&
    pendingOptions.engineCode === currentOptions.engineCode
  ) {
    conflicts.push({
      code: currentOptions.engineCode,
      key: 'engine',
      name: currentOptions.engineCode,
      priceAdd: 0
    });
  }

  if (
    currentOptions.exteriorColorCode &&
    newFinalState.exteriorColorCode !== currentOptions.exteriorColorCode &&
    pendingOptions.exteriorColorCode === currentOptions.exteriorColorCode
  ) {
    conflicts.push({
      code: currentOptions.exteriorColorCode,
      key: 'exterior',
      name: currentOptions.exteriorColorCode,
      priceAdd: 0
    });
  }

  if (
    currentOptions.interiorColorCode &&
    newFinalState.interiorColorCode !== currentOptions.interiorColorCode &&
    pendingOptions.interiorColorCode === currentOptions.interiorColorCode
  ) {
    conflicts.push({
      code: currentOptions.interiorColorCode,
      key: 'interior',
      name: currentOptions.interiorColorCode,
      priceAdd: 0
    });
  }

  const currentPkgSet = new Set(currentOptions.packageOptionCodes ?? []);
  const pendingPkgSet = new Set(pendingOptions.packageOptionCodes ?? []);
  const finalPkgSet = new Set(newFinalState.packageOptionCodes ?? []);

  for (const code of currentPkgSet) {
    if (!finalPkgSet.has(code) && pendingPkgSet.has(code)) {
      conflicts.push({ code, key: 'package', name: code, priceAdd: 0 });
    }
  }

  return conflicts;
}

/**
 * Identifies items in currentOptions that have count = 0 in the pending
 * response, meaning they cannot coexist with the new pick (or without the
 * deselected item).
 */
export function detectUnavailableRemovals(
  currentOptions: CustomizeRequest,
  pendingResponse: CustomizeResponse
): AutoSelectItem[] {
  const removals: AutoSelectItem[] = [];

  if (currentOptions.exteriorColorCode) {
    const found = pendingResponse.exteriorColors.find(
      c => c.code === currentOptions.exteriorColorCode
    );

    if (found && found.count === 0) {
      removals.push({
        code: currentOptions.exteriorColorCode,
        key: 'exterior',
        name: found.name,
        priceAdd: found.priceAdd
      });
    }
  }

  if (currentOptions.interiorColorCode) {
    const found = pendingResponse.interiorColors.find(
      c => c.code === currentOptions.interiorColorCode
    );

    if (found && found.count === 0) {
      removals.push({
        code: currentOptions.interiorColorCode,
        key: 'interior',
        name: found.name,
        priceAdd: found.priceAdd
      });
    }
  }

  if (currentOptions.driveType) {
    const found = pendingResponse.driveTypes.find(
      d => d.name === currentOptions.driveType
    );

    if (found && found.count === 0) {
      removals.push({
        code: currentOptions.driveType,
        key: 'driveType',
        name: found.name,
        priceAdd: found.priceAdd
      });
    }
  }

  if (currentOptions.engineCode) {
    const found = pendingResponse.engines.find(
      e => e.code === currentOptions.engineCode
    );

    if (found && found.count === 0) {
      removals.push({
        code: currentOptions.engineCode,
        key: 'engine',
        name: found.name,
        priceAdd: found.priceAdd
      });
    }
  }

  for (const code of currentOptions.packageOptionCodes ?? []) {
    const found = pendingResponse.packageOptions.find(p => p.code === code);

    if (found && found.count === 0) {
      removals.push({
        code,
        key: 'package',
        name: found.name,
        priceAdd: found.priceAdd
      });
    }
  }

  return removals;
}

/**
 * Returns true if the item is the explicit user pick (the trigger), as opposed
 * to a forced auto-select side effect. Checks whether the item's code matches
 * the value set in pendingOptions for that key.
 */
export function isPendingTrigger(
  item: AutoSelectItem,
  pendingOptions: CustomizeRequest
): boolean {
  switch (item.key) {
    case 'driveType':
      return pendingOptions.driveType === item.code;
    case 'engine':
      return pendingOptions.engineCode === item.code;
    case 'exterior':
      return pendingOptions.exteriorColorCode === item.code;
    case 'interior':
      return pendingOptions.interiorColorCode === item.code;
    case 'package':
      return (pendingOptions.packageOptionCodes ?? []).includes(item.code);
    default:
      return false;
  }
}

export function computeStrictRemovals(
  currentOptions: CustomizeRequest,
  newFinalState: CustomizeRequest,
  triggerKey: AutoSelectKey,
  data: CustomizeResponse
): AutoSelectItem[] {
  const removals: AutoSelectItem[] = [];

  if (
    triggerKey !== 'driveType' &&
    currentOptions.driveType &&
    newFinalState.driveType !== currentOptions.driveType
  ) {
    removals.push(
      buildAutoSelectItem('driveType', currentOptions.driveType, data)
    );
  }

  if (
    triggerKey !== 'engine' &&
    currentOptions.engineCode &&
    newFinalState.engineCode !== currentOptions.engineCode
  ) {
    removals.push(
      buildAutoSelectItem('engine', currentOptions.engineCode, data)
    );
  }

  if (
    triggerKey !== 'exterior' &&
    currentOptions.exteriorColorCode &&
    newFinalState.exteriorColorCode !== currentOptions.exteriorColorCode
  ) {
    removals.push(
      buildAutoSelectItem('exterior', currentOptions.exteriorColorCode, data)
    );
  }

  if (
    triggerKey !== 'interior' &&
    currentOptions.interiorColorCode &&
    newFinalState.interiorColorCode !== currentOptions.interiorColorCode
  ) {
    removals.push(
      buildAutoSelectItem('interior', currentOptions.interiorColorCode, data)
    );
  }

  const finalPkgCodes = new Set(newFinalState.packageOptionCodes ?? []);

  for (const code of currentOptions.packageOptionCodes ?? []) {
    if (!finalPkgCodes.has(code)) {
      removals.push(buildAutoSelectItem('package', code, data));
    }
  }

  return removals;
}

export function countSelectedCategories(sel: CustomizeRequest | null): number {
  if (!sel) return 0;

  let count = 0;

  if (sel.driveType) count++;
  if (sel.engineCode) count++;
  if (sel.exteriorColorCode) count++;
  if (sel.interiorColorCode) count++;
  if ((sel.packageOptionCodes?.length ?? 0) > 0) count++;

  return count;
}

export function removeFromSelection(
  sel: CustomizeRequest,
  key: AutoSelectKey,
  code: string
): CustomizeRequest {
  switch (key) {
    case 'driveType':
      return { ...sel, driveType: undefined };
    case 'engine':
      return { ...sel, engineCode: undefined };
    case 'exterior':
      return { ...sel, exteriorColorCode: undefined };
    case 'interior':
      return { ...sel, interiorColorCode: undefined };
    case 'package':
      return {
        ...sel,
        packageOptionCodes: (sel.packageOptionCodes ?? []).filter(
          c => c !== code
        )
      };
    default:
      throw new Error(`Unknown key: ${key}`);
  }
}
